<template>
  <div v-if="!device?.isVirtual" class="list-group card-item" ref="lightItem" style="border-radius: 1.2rem; margin-top: 1px;" @click="onLightDevicePowerClicked" v-touch:press="onTouchDown" v-touch:drag="onTouchDrag" v-touch:release="onTouchRelease" v-touch:hold="onTouchHold">
    <div class="list-group-item d-flex align-items-center" :style="`height: 4rem; padding: 2px 0px; background: ${getPower ? `linear-gradient(to right, ${buttonColor} ${percent}%, ${buttonAnotherColor} ${percent}%)` : 'inherits'};`">
      <i class="fa-regular fa-lightbulb ms-3 me-2" />
      <div class="flex-fill" style="font-size: 0.8rem">
        {{ lampDisplayName }}
        <br>
        {{ getPower ? currentBrightText : "" }}
      </div>
      <div class="mx-0 h-100" @click.stop="onSettingClicked">
        <i class="fa-solid fa-gear" style="font-size: 0.8rem; margin-top: 24px; width:40px; text-align: center" />
      </div>
      <!-- <div class="mx-1" @click.stop="onLightDeviceInfoClicked">
        <i class="fa fa-ellipsis" />
      </div> -->
    </div>
  </div>

  <div v-else class="list-group" ref="lightItem" style="border-radius: 1.2rem; margin-top: 1px;" @click="onLightDevicePowerClicked" v-touch:hold="onTouchHold">
    <div class="list-group-item d-flex align-items-center" :style="`height: 4rem; padding: 2px 0px; background: ${getPower ? `linear-gradient(to right, #ffe082 100%, #fff8e1 100%)` : 'inherits'};`">
      <i class="fa-regular fa-lightbulb ms-3 me-2" />
      <div class="flex-fill" style="font-size: 0.8rem">
        {{ device.name }}{{ virtualButtonSuffix }}
      </div>
      <div class="mx-0 h-100" @click.stop="onSettingClicked">
        <i :class="`fa-solid fa-gear`" style="font-size: 0.8rem; margin-top: 24px; width:40px; text-align: center" />
      </div>
      <!-- <div class="mx-1" @click.stop="onLightDeviceInfoClicked">
        <i class="fa fa-ellipsis" />
      </div> -->
    </div>
  </div>
        
  <LightDeviceItemModal
    :visible="lightDeviceItemModal.visible"
    :cancelCallback="lightDeviceItemModal.hide"
    :roomName="props.roomName"
    :roomId="props.roomId"
    :deviceId="props.deviceId"
    :handlePowerClick="onLightDevicePowerClicked"
  />
</template>

<script setup>
import Axios, { SmarthomeApi } from '@/api';
import { reactive, defineProps, getCurrentInstance, computed, onMounted, watch, ref } from 'vue';
import { useStore } from 'vuex';
import { DeviceType, PowerState, ButtonConfig, lightButtonColors, lightButtonAnotherColors } from '@/common/constants';
import { publishVirtualButtonLampsCmdC } from '@/common/utils/virtualButtonMqtt';
import {
  hasPhysicalMappingMembers,
  isPhysicalMappingHostDevice,
  onPhysicalMappingHostControl,
  preparePhysicalMappingMemberDirectControl,
} from '@/common/utils/physicalMappingMember';
import LightDeviceItemModal from './LightDeviceItemModal.vue';
import useModal from '@/common/hooks/useModal';
import useMqttControl from '@/common/hooks/useMqttControl';
import useDeviceTouch from '@/common/hooks/useDeviceTouch';
import { resolveBoundButtonDisplayName } from '@/common/utils/boundButtonLabel';

const mqttControl = reactive(useMqttControl());
const props = defineProps(['roomId', 'roomName', 'deviceId']);
const store = useStore();
const lightDeviceItemModal = reactive(useModal());
const { emitter } = getCurrentInstance().appContext.config.globalProperties;
const lightItem = ref(null);
const percent = ref(0.0);
const currentBright = ref(0);
const touchStartX = ref(0);
const touchStartY = ref(0);
const isScrollGesture = ref(false);
const { 
  handlePower, 
  handleTouchDown, 
  handleTouchDrag, 
  handleTouchRelease, 
  handleTouchHold 
} = useDeviceTouch(props.roomId);

const zone = computed(() => {
  if (store.state.home.zones.length > 0) {
    return store.getters['home/getCurrentZone'];
  } else return null;
});
const group = computed(() => {
  return zone.value.groups.find((g) => g._id === props.roomId);
});
const devices = computed(() => {
  return group.value ? group.value.devices : undefined;
});
const virtualDevices = computed(() => {
  return devices.value?.filter(item => item.isVirtual && item._id != props.deviceId);
});
const device = computed(() => {
  return devices.value.find(
    (d) => d._id === props.deviceId,
  );
});

/** 예: (가상버튼) / (가상버튼-세번째방 연결) — 흡수 hostName 또는 포트 장치 이름 */
const virtualButtonSuffix = computed(() => {
  const name = resolveBoundButtonDisplayName(
    device.value,
    store.getters['home/getZones'],
  );
  if (name) {
    return `(가상버튼-${name} 연결)`;
  }
  return '(가상버튼)';
});

const lampView = computed(() => {
  const d = device.value;
  const flat = d?.item ?? {};
  if (d?.onVirtual === true) {
    const st = flat?.state ?? {};
    // 대표버튼 UI: item.state 만 사용 (vc로 켜진 item.onoff 로 fallback 하지 않음)
    return {
      onoff: st?.onoff ?? 0,
      bright: st?.bright ?? flat?.bright ?? 0,
      color: st?.color ?? flat?.color ?? 0,
    };
  }
  return {
    onoff: flat?.onoff ?? 0,
    bright: flat?.bright ?? 0,
    color: flat?.color ?? 0,
  };
});

const isMappingButton = ref(false);
const lampDisplayName = computed(() => {
  const name = device.value?.name || '';
  if (device.value?.isVirtual) return name;
  return isMappingButton.value ? `${name}(매핑버튼)` : name;
});

const getPower = computed(() => {
  return lampView.value.onoff === PowerState.On;
});
const getBright = computed(() => {
  return lampView.value.bright;
});
const getColor = computed(() => {
  return lampView.value.color;
});

const buttonColor = computed(() => {
  const index = getColor.value / 2;
  return getColor.value !== undefined && lightButtonColors.length > index ? lightButtonColors[index] : "#ffe082";
});
const buttonAnotherColor = computed(() => {
  const index = getColor.value / 2;
  return getColor.value !== undefined && lightButtonAnotherColors.length > index ? lightButtonAnotherColors[index] : "#fff8e1";
});

const currentBrightText = computed(() => {
  if (device?.value?.isVirtual) {
    return "";
  }
  return `${parseInt(getBright.value)}%`;
})

watch(getBright, () => {
  currentBright.value = getBright.value;
  percent.value = (getBright.value - 10) / 90 * 100;
})

const refreshMappingButtonLabel = async () => {
  if (device.value?.isVirtual || device.value?._id == null) {
    isMappingButton.value = false;
    return;
  }
  isMappingButton.value = await hasPhysicalMappingMembers(device.value._id);
};

onMounted(() => {
  currentBright.value = getBright.value;
  percent.value = (currentBright.value - 10) / 90 * 100;
  refreshMappingButtonLabel();
});

watch(() => device.value?._id, () => {
  refreshMappingButtonLabel();
});

const requestChangeVirtualDevicePower = async (params, id) => {
  return new Promise(async (resolve, reject) => {
    try {
      const result = await Axios.patch(SmarthomeApi.CHANGE_VIRTUAL_DEVICE_POWER.replace(":id", id), params);
      const beforeDevice = virtualDevices.value.find(item => item.item.onoff > 0);

      if (result.resultCode === '0000') {
        if (beforeDevice) {
          store.commit('home/updateDevice', {
            groupId: props.roomId,
            id: beforeDevice._id,
            data: {
              item: { onoff: 0 }
            },
          });
        }
        store.commit('home/updateDevice', {
          groupId: props.roomId,
          id: id,
          data: {
            item: result.resultData.item
          },
        });
        resolve(true);
        return;
      }

      reject(false);
      emitter.emit('showPopAlert', {
        description: result.resultMessage,
      });
    } catch (e) {
      reject(false);
      console.log(e);
      emitter.emit('showPopAlert', {
        description: '가상버튼 ON/OFF에 실패했습니다.',
      });
    }
  });
};

const requestVirtualDevicePower = async (params, id) => {
  return new Promise(async (resolve, reject) => {
    try {
      const turnOn = params?.onoff == 1 || params?.onoff === true;
      // API/vc echo 전에 차일드 대표버튼이 켜져 보이지 않도록 즉시 고정
      if (turnOn) {
        (devices.value || []).forEach((d) => {
          if (d?.isVirtual || d?.type !== 1) return;
          store.commit('home/updateDevice', {
            groupId: props.roomId,
            id: d._id,
            data: { onVirtual: true, item: { state: { onoff: 0 } } },
          });
        });
      }

      const result = await Axios.patch(SmarthomeApi.VIRTUAL_DEVICE_POWER.replace(":id", id), params);
      if (result.resultCode === '0000') {
        console.log(result);

        const prevId =
          result.resultData?.offItem?._id ??
          params?.onVirtualDeviceId ??
          null;

        if (result.resultData.onItem && result.resultData.offItem) {
          store.commit('home/updateDevice', {
            groupId: props.roomId,
            id: result.resultData.offItem._id,
            data: {
              item: { onoff: 0 }
            },
          });
          store.commit('home/updateDevice', {
            groupId: props.roomId,
            id: result.resultData.onItem._id,
            data: {
              item: result.resultData.onItem.item
            },
          });

          const onMemberDeviceIds = result.resultData.onItemMemberDeviceIds ?? [];
          const offMemberDeviceIds = result.resultData.offItemMemberDeviceIds ?? [];
          onMemberDeviceIds.forEach((memberId) => {
            store.commit('home/updateDevice', {
              groupId: props.roomId,
              id: memberId,
              data: { onVirtual: true, item: { state: { onoff: 0 } } },
            });
          });
          offMemberDeviceIds.forEach((memberId) => {
            store.commit('home/updateDevice', {
              groupId: props.roomId,
              id: memberId,
              data: { onVirtual: false, item: { onoff: 0 } },
            });
          });

        } else {
          store.commit('home/updateDevice', {
            groupId: props.roomId,
            id: id,
            data: {
              item: result.resultData.item
            },
          });

          const memberDeviceIds = result.resultData.memberDeviceIds ?? [];
          const onoff = result.resultData.item?.onoff ?? params?.onoff;
          const isOn = onoff == 1;
          memberDeviceIds.forEach((memberId) => {
            store.commit('home/updateDevice', {
              groupId: props.roomId,
              id: memberId,
              data: {
                onVirtual: isOn,
                ...(isOn
                  ? { item: { state: { onoff: 0 } } }
                  : { item: { onoff: 0 } }),
              },
            });
          });
        }

        // cs/vc 보드(M24/M22/M02): API vc 사용. 구형만 아래 cmd c 추가 발행.
        await publishVirtualButtonLampsCmdC({
          mqttControl,
          store,
          virtualDeviceId: id,
          turnOn,
          prevVirtualDeviceId: turnOn ? prevId : null,
        }).catch((e) => {
          console.error('가상버튼 MQTT c 발행 실패:', id, e);
        });

        resolve(true);
        return;
      }

      reject(false);
      emitter.emit('showPopAlert', {
        description: result.resultMessage,
      });
    } catch (e) {
      reject(false);
      console.log(e);
      emitter.emit('showPopAlert', {
        description: '가상버튼 ON/OFF에 실패했습니다.',
      });
    }
  });
};

const onLightDevicePowerClicked = (e) => {
  handlePower(async () => {
    try {
      if (zone.value.isEditMode) {
        emitter.emit('showPopAlert', {
          description: '세팅중인 영역의 단말은 제어 할 수 없습니다.',
        });
        return;
      }

      emitter.emit('showLoading');
      
      if (device.value?.isVirtual) {
        const params = { onoff: getPower.value ? PowerState.Off : PowerState.On };
        const onVirtualDevice = virtualDevices.value.find(item => item.item.onoff > 0);
        if (onVirtualDevice) {
          params["onVirtualDeviceId"] = onVirtualDevice._id;
        }
        await requestVirtualDevicePower(params, device.value._id).catch((e) => {
          console.error('가상버튼 제어 실패:', device.value._id, e);
        });
      } else {
        const data = {
          ports: device.value.item.ports.map((p) => p),
          onoff: device.value?.onVirtual === true ? ((device.value.item.state?.onoff ?? 0) === 1 ? PowerState.Off : PowerState.On) : (getPower.value ? PowerState.Off : PowerState.On),
          bright: device.value.item.bright,
          color: device.value.item.color ?? 0,
        };

        // 호스트 보드 MQTT 발행. cross 멤버는 호스트가 zone cm 으로 전달 (앱 peer cs 이중발행 방지)
        mqttControl.onPublish(device.value.controllerId, device.value._id, {
          serial: device.value.controllerId,
          cmd: 'c',
          type: device.value.type,
          data: data,
        });

        const isHost = await isPhysicalMappingHostDevice(device.value._id);
        if (isHost) {
          await onPhysicalMappingHostControl(
            store,
            device.value._id,
            data.onoff,
            data.bright,
            data.color,
          );
        } else {
          preparePhysicalMappingMemberDirectControl(
            store,
            device.value._id,
            data.onoff,
            data.bright,
            data.color,
          );
        }
      }
      setTimeout(() => emitter.emit('hideLoading'), ButtonConfig.delay);
    } catch (e) {
      emitter.emit('hideLoading');
    }
  });
};

const onLightDeviceInfoClicked = (e) => {
  if (zone.value.isEditMode) {
    emitter.emit('showPopAlert', {
      description: '세팅중인 영역의 단말은 제어 할 수 없습니다.',
    });
    return;
  }
  lightDeviceItemModal.show();
};

const onSettingClicked = (e) => {
  onLightDeviceInfoClicked();
}

const brightChange = async () => {
  if (zone.value.isEditMode) {
    emitter.emit('showPopAlert', {
      description: '세팅중인 영역의 단말은 제어 할 수 없습니다.',
    });
    return;
  }

  emitter.emit('showLoading');
  try {
    const onoff = device.value?.onVirtual === true
      ? ((device.value.item.state?.onoff ?? 0) === 1 ? PowerState.Off : PowerState.On)
      : device.value.item.onoff;
    const bright = parseInt(currentBright.value, 10);
    const color = device.value.item.color ?? 0;

    mqttControl.onPublish(device.value.controllerId, device.value._id, {
      serial: device.value.controllerId,
      cmd: 'c',
      type: 1,
      data: {
        ports: device.value.item.ports.map((p) => p),
        onoff,
        bright,
        color,
      },
    });

    const isHost = await isPhysicalMappingHostDevice(device.value._id);
    if (isHost) {
      await onPhysicalMappingHostControl(store, device.value._id, onoff, bright, color);
    } else {
      preparePhysicalMappingMemberDirectControl(store, device.value._id, onoff, bright, color);
    }
    setTimeout(() => emitter.emit('hideLoading'), ButtonConfig.delay);
  } catch (e) {
    emitter.emit('hideLoading');
  }
};

const onTouchDown = (e) => {
  if (device.value.isVirtual) {
    return;
  }

  const clientX = e.targetTouches ? e.targetTouches[0]?.clientX : e.clientX;
  const clientY = e.targetTouches ? e.targetTouches[0]?.clientY : e.clientY;
  touchStartX.value = clientX;
  touchStartY.value = clientY;
  isScrollGesture.value = false;

  if (getPower.value) {
    handleTouchDown(e);
  }
}

const onTouchDrag = (e) => {
  if (device.value.isVirtual) {
    return;
  }

  const currentX = e.targetTouches ? e.targetTouches[0]?.clientX : e.clientX;
  const currentY = e.targetTouches ? e.targetTouches[0]?.clientY : e.clientY;
  const deltaX = Math.abs(currentX - touchStartX.value);
  const deltaY = Math.abs(currentY - touchStartY.value);
  if (deltaY > deltaX) {
    isScrollGesture.value = true;
    return;
  }

  handleTouchDrag(e, (screenX, startX) => {
    percent.value = percent.value + (screenX - startX) / lightItem.value.clientWidth * 100.0;
    percent.value = percent.value <= 0 ? 0 : percent.value;
    percent.value = percent.value >= 100 ? 100 : percent.value;
    currentBright.value = parseInt((9 * percent.value * 0.01) + 1) * 10;
  });
}

const onTouchRelease = (e) => {
  if (device.value.isVirtual) {
    return;
  }

  if (isScrollGesture.value) {
    handleTouchRelease(e, () => {}, { skipClickOnly: true });
  } else {
    handleTouchRelease(e, brightChange);
  }
}

const onTouchHold = (e) => {
  handleTouchHold(e, onLightDeviceInfoClicked);
}
</script>

<style scoped>
.card-item {
  background-color: #ffffff;
  border: 1px solid #e9ecef;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
}

.card-item:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  transform: translateY(-2px);
}
</style>
